/* Model personality — 44px field rows. Collapsed: icon, name, one-line preview,
   character meter and chevron. Open: an expandable, scrollable textarea, or the
   emoji editor with quick-add. */

import { useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon, Select } from '../kit';
import { C } from '../tokens';
import { font as f } from '../style';
import { QUICK_EMOJI, TIMEZONES, splitEmojis, type FieldDef } from './model';

const rowHead = { display: 'flex', alignItems: 'center', gap: 10, height: 44, padding: '0 14px', boxSizing: 'border-box' } as const;
const ellipsis = { flex: '1 1 auto', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } as const;

function Head({ def, open, onToggle, preview, filled, right }: { def: FieldDef; open: boolean; onToggle: () => void; preview: string; filled: boolean; right: React.ReactNode }) {
  const { t } = useTranslation('assistantCompact');
  return (
    <div
      role="button"
      tabIndex={0}
      aria-expanded={open}
      onClick={onToggle}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(); } }}
      className="cpt-hover-soft"
      style={{ ...rowHead, cursor: 'pointer' }}
    >
      <Icon name={def.icon} size={14} color={C.purpleLt} style={{ width: 16 }} />
      <span style={{ font: f(500, 13), color: C.text, whiteSpace: 'nowrap', flex: 'none' }}>{t(`mp.f.${def.id}.t`)}</span>
      <span title={t(`mp.f.${def.id}.tip`)} style={{ display: 'inline-flex', flex: 'none' }}><Icon name="info" size={12} color={C.muted} /></span>
      <span style={{ ...ellipsis, font: f(400, 12), color: filled ? C.sub : C.muted }}>{open ? '' : preview}</span>
      {right}
      <Icon name="chevron-down" size={14} color={C.muted} style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }} />
    </div>
  );
}

/** Row wrapper: divider above every row but the first, a darker tint when open. */
export function FieldShell({ first, open, children }: { first: boolean; open: boolean; children: React.ReactNode }) {
  return <div style={{ borderTop: first ? 0 : `1px solid ${C.divider}`, background: open ? 'var(--om-panel)' : 'transparent' }}>{children}</div>;
}

export function TextField({ def, value, onChange, open, onToggle }: { def: FieldDef; value: string; onChange: (v: string) => void; open: boolean; onToggle: () => void }) {
  const { t } = useTranslation('assistantCompact');
  const [big, setBig] = useState(false);
  const max = def.max ?? 1000;
  const len = value.length;
  const hi = len / max >= 0.9;
  // Bullet lists read as one line: strip the markers, join with " · ".
  const prev = value.split('\n').map((l) => l.replace(/^\s*[*•\-·]\s*/, '').trim()).filter(Boolean).join(' · ');
  return (
    <>
      <Head
        def={def}
        open={open}
        onToggle={onToggle}
        filled={!!prev}
        preview={prev || t('mp.empty')}
        right={
          <>
            <div style={{ width: 40, height: 3, borderRadius: 2, background: C.segActive, flex: 'none', overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${Math.min(100, Math.round((len / max) * 100))}%`, background: hi ? C.purplePale : C.purple }} />
            </div>
            <span title={hi ? t('mp.nearLimit') : undefined} style={{ flex: 'none', width: 66, textAlign: 'right', font: f(500, 11), fontVariantNumeric: 'tabular-nums', color: hi ? C.purplePale : C.sub, whiteSpace: 'nowrap' }}>{len}/{max}</span>
          </>
        }
      />
      {open && (
        <div style={{ padding: '0 14px 12px 40px' }}>
          <div style={{ position: 'relative' }}>
            <textarea
              value={value}
              maxLength={max}
              onChange={(e) => onChange(e.target.value.slice(0, max))}
              placeholder={t('mp.placeholder')}
              className="cpt-thin-scroll"
              style={{ display: 'block', width: '100%', height: big ? 440 : 160, minHeight: 96, boxSizing: 'border-box', resize: 'vertical', overflowY: 'auto', background: C.input, border: `1px solid ${C.border}`, borderRadius: 6, padding: '8px 38px 8px 10px', color: C.text, font: f(400, 12.5, 1.55), outline: 'none' }}
            />
            <button
              type="button"
              onClick={() => setBig((b) => !b)}
              title={big ? t('mp.shrink') : t('mp.expand')}
              className="cpt-hover-text"
              style={{ position: 'absolute', top: 6, right: 8, width: 24, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, border: 0, borderRadius: 5, background: C.card, color: C.sub, cursor: 'pointer' }}
            >
              <Icon name={big ? 'minimize-2' : 'maximize-2'} size={13} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}

export function EmojiField({ def, value, onChange, open, onToggle }: { def: FieldDef; value: string[]; onChange: (v: string[]) => void; open: boolean; onToggle: () => void }) {
  const { t } = useTranslation('assistantCompact');
  const [draft, setDraft] = useState('');
  const n = value.length;
  const add = (list: string[]) => {
    const next = [...value];
    for (const e of list) if (!next.includes(e)) next.push(e);
    onChange(next);
  };
  // Emojis typed or pasted are picked out straight away; other text (":)", "<3")
  // waits for Enter or a comma.
  const onType = (v: string) => {
    const found = splitEmojis(v);
    if (found.length) { add(found); setDraft(''); } else setDraft(v);
  };
  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if ((e.key === 'Enter' || e.key === ',') && draft.trim()) {
      e.preventDefault();
      add(draft.split(',').map((s) => s.trim()).filter(Boolean));
      setDraft('');
    } else if (e.key === 'Backspace' && draft === '' && n) {
      onChange(value.slice(0, -1));
    }
  };
  return (
    <>
      <Head
        def={def}
        open={open}
        onToggle={onToggle}
        filled={n > 0}
        preview={n ? value.join(' ') : t('mp.noEmojis')}
        right={<span style={{ flex: 'none', width: 66, textAlign: 'right', font: f(500, 11), fontVariantNumeric: 'tabular-nums', color: C.sub, whiteSpace: 'nowrap' }}>{t('mp.emojiCount', { count: n })}</span>}
      />
      {open && (
        <div style={{ padding: '0 14px 12px 40px', display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', minHeight: 34, boxSizing: 'border-box', padding: '4px 6px', background: C.input, border: `1px solid ${C.border}`, borderRadius: 6 }}>
            {value.map((ch, i) => (
              <button key={ch + i} type="button" onClick={() => onChange(value.filter((_, j) => j !== i))} title={t('mp.emoji.remove')} className="cpt-hover" style={{ height: 24, minWidth: 26, padding: '0 2px', border: 0, borderRadius: 5, background: 'transparent', color: C.text, fontSize: splitEmojis(ch).length ? 15 : 12, lineHeight: 1, cursor: 'pointer' }}>{ch}</button>
            ))}
            <input
              value={draft}
              onChange={(e) => onType(e.target.value)}
              onKeyDown={onKey}
              onBlur={() => { if (draft.trim()) { add(draft.split(',').map((s) => s.trim()).filter(Boolean)); setDraft(''); } }}
              placeholder={t('mp.emoji.placeholder')}
              style={{ flex: 1, minWidth: 140, height: 24, padding: '0 6px', background: 'transparent', border: 0, outline: 'none', color: C.text, font: f(400, 12.5) }}
            />
            {n > 0 && (
              <button type="button" onClick={() => onChange([])} className="cpt-hover-text" style={{ height: 22, flex: 'none', padding: '0 8px', border: 0, borderRadius: 5, background: 'transparent', color: C.sub, font: f(500, 11.5), cursor: 'pointer' }}>{t('mp.emoji.clear')}</button>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
            <span style={{ font: f(600, 10), letterSpacing: '.06em', textTransform: 'uppercase', color: C.muted, marginRight: 6, whiteSpace: 'nowrap' }}>{t('mp.emoji.quick')}</span>
            {QUICK_EMOJI.map((ch) => {
              const on = value.includes(ch);
              return (
                <button
                  key={ch}
                  type="button"
                  title={on ? t('mp.emoji.remove') : t('mp.emoji.add')}
                  aria-pressed={on}
                  onClick={() => onChange(on ? value.filter((e) => e !== ch) : [...value, ch])}
                  className="cpt-hover-line"
                  style={{ width: 28, height: 28, padding: 0, borderRadius: 6, border: `1px solid ${on ? 'color-mix(in srgb, var(--om-accent) 60%, transparent)' : C.border}`, background: on ? C.tint20 : C.input, fontSize: 15, lineHeight: 1, cursor: 'pointer' }}
                >{ch}</button>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
}

export function TimezoneRow({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const { t } = useTranslation('assistantCompact');
  // Keep a stored zone that isn't in the curated list selectable.
  const opts = TIMEZONES.some((z) => z.id === value) ? TIMEZONES : [{ id: value, label: value }, ...TIMEZONES];
  return (
    <div style={rowHead}>
      <Icon name="globe" size={14} color={C.purpleLt} style={{ width: 16 }} />
      <span style={{ font: f(500, 13), color: C.text, whiteSpace: 'nowrap' }}>{t('mp.tz.label')}</span>
      <span style={{ ...ellipsis, font: f(400, 12), color: C.muted }}>{t('mp.tz.help')}</span>
      <Select value={value} options={opts} onChange={onChange} width={210} />
    </div>
  );
}
